import React, { useState, useEffect, useRef } from "react";
import { useParams, useOutletContext, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Users, ChevronDown, ChevronUp, Plus, Minus, Check, X,
  Package, MapPin, Trash2, Edit3, Clock, DollarSign, Percent, Feather, Copy, Shield, Search, Pencil
} from "lucide-react";
import toast from "react-hot-toast";

import { useLanguage } from "../LanguageProvider";
import { useAuth } from "../AuthProvider";
import { supabase } from "../services/supabase";
import { useSessionData } from "../hooks/useSessionData";
import { Header } from "../components/layout/Header";
import { ConfirmModal } from "../components/common/ConfirmModal";
import { MultiSelectionModal } from "../components/common/MultiSelectionModal";
import { PageLoader } from "../components/common/LoadingStates";
import { calculateTotals, calculateBalances, getFieldHoursForExpense } from "../utils/calculator";
import { normalizeString } from "../utils/stringUtils";
import swishDark from "../assets/Swish Logo Primary Dark-BG SVG.svg";
import swishLight from "../assets/Swish Logo Primary Light-BG SVG.svg";

export const SessionDetail = () => {
  const { id: sessionId } = useParams();
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const lastToastSessionId = useRef(null);
  const calcTimeoutRef = useRef(null);

  const { allParticipants, sessions, refreshGlobalData, refreshUnpaidUsers } = useOutletContext();
  const {
    sessionParticipants, expenses, payments, fieldHours, excludeBoxUsers,
    loading, error, isNotFound, refreshSession, setFieldHours, setExcludeBoxUsers
  } = useSessionData(sessionId);

  // Handle errors or missing session
  useEffect(() => {
    if (error) {
      toast.error(t(error) || error);
    }

    if (isNotFound && lastToastSessionId.current !== sessionId) {
      lastToastSessionId.current = sessionId;
      toast.error(t("session_not_found"));
      navigate("/", { replace: true });
    }
  }, [error, isNotFound, navigate, t, sessionId]);

  const [expandParticipants, setExpandParticipants] = useState(false);
  const [isParticipantModalOpen, setIsParticipantModalOpen] = useState(false);
  const [selectedParticipantIds, setSelectedParticipantIds] = useState([]);
  const [isAddingParticipants, setIsAddingParticipants] = useState(false);

  const [newBox, setNewBox] = useState({ description: "", totalAmount: "", totalQuantity: "" });
  const [newField, setNewField] = useState({ fieldName: "", hours: "2", pricePerHour: "" });
  const [editingUsed, setEditingUsed] = useState(null);
  const [usedValue, setUsedValue] = useState("");
  const [expandedField, setExpandedField] = useState(null);
  const [editingFieldExpense, setEditingFieldExpense] = useState(null);
  const [editFieldValues, setEditFieldValues] = useState({ fieldName: "", hours: "", pricePerHour: "" });
  const [isBoxFormOpen, setIsBoxFormOpen] = useState(window.innerWidth >= 768);
  const [isFieldFormOpen, setIsFieldFormOpen] = useState(window.innerWidth >= 768);
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false, title: "", message: "", onConfirm: () => { }, type: "danger"
  });
  const [searchQuery, setSearchQuery] = useState("");
  const searchRef = useRef(null);

  const handleSearchFocus = () => {
    // Only auto-scroll on mobile devices
    if (window.innerWidth <= 768) {
      setTimeout(() => {
        searchRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 300); // Small delay to allow mobile keyboard to begin appearing
    }
  };

  const selectedSessionData = sessions.find((s) => s.id === sessionId);
  const adminFeePercent = selectedSessionData?.admin_fee_percent || 0;

  // Ownership check: only session creator can edit
  const isOwner = user?.id && selectedSessionData?.created_by && user.id === selectedSessionData.created_by;

  // Computations
  const {
    totalBoxCost, totalFieldCost, totalExpenses, boxes, fields,
    totalUsedQuantity, totalTotalQuantity, totalHours, adminFee, adminFeePercent: feePercent
  } = calculateTotals(expenses, fieldHours, adminFeePercent);

  const balances = calculateBalances({
    sessionParticipants,
    expenses,
    fieldHours,
    excludeBoxUsers,
    payments,
    adminFeePercent
  });

  // Admin fee is split equally across all participants — show the per-person share
  const adminFeePerPerson = sessionParticipants.length > 0
    ? Math.round((adminFee / sessionParticipants.length) * 100) / 100
    : 0;
  const feePerPersonLabel = Number.isInteger(adminFeePerPerson)
    ? adminFeePerPerson.toString()
    : adminFeePerPerson.toFixed(2);

  const paidCount = balances.filter(b => b.hasPaid).length;

  const filteredBalances = balances.filter(person =>
    normalizeString(person.name).includes(normalizeString(searchQuery))
  );

  useEffect(() => {
    if (!sessionId || balances.length === 0) return;

    const calculateAndSaveAmounts = async () => {
      const amountsToSave = [];

      balances.forEach((person) => {
        let fieldCost = 0;
        let boxCost = 0;

        fields.forEach((exp) => {
          const tField = parseFloat(exp.total_amount);
          const hoursData = getFieldHoursForExpense(fieldHours, exp.id);
          const totalHoursUsed = hoursData.reduce((sum, fh) => sum + parseFloat(fh.hours_played), 0);
          if (totalHoursUsed > 0) {
            const userHours = hoursData.find((fh) => fh.participant_id === person.id);
            if (userHours) {
              const userHour = parseFloat(userHours.hours_played);
              fieldCost += tField * (userHour / totalHoursUsed);
            }
          }
        });

        if (!excludeBoxUsers[person.id]) {
          const peoplePayingBoxes = balances.filter((p) => !excludeBoxUsers[p.id]);
          if (peoplePayingBoxes.length > 0 && totalBoxCost > 0) {
            boxCost = totalBoxCost / peoplePayingBoxes.length;
          }
        }

        // Add admin fee split equally among all participants
        let adminFeeCost = 0;
        if (adminFee > 0 && balances.length > 0) {
          adminFeeCost = Math.round((adminFee / balances.length) * 100) / 100;
        }

        const totalForPerson = fieldCost + boxCost + adminFeeCost;
        // Include everyone in the save array, even if amount is 0, to clear stale values
        amountsToSave.push({ id: person.id, amount: totalForPerson });
      });

      for (const item of amountsToSave) {
        const existingPayment = payments.find((p) => p.participant_id === item.id);
        const currentAmount = existingPayment?.amount_owed || 0;

        if (Math.abs(currentAmount - item.amount) > 0.01) {
          await supabase
            .from("session_payments")
            .update({ amount_owed: item.amount })
            .eq("session_id", sessionId)
            .eq("participant_id", item.id);
        }
      }
    };

    if (calcTimeoutRef.current) {
      clearTimeout(calcTimeoutRef.current);
    }

    calcTimeoutRef.current = setTimeout(() => {
      calculateAndSaveAmounts();
    }, 300);

    return () => {
      if (calcTimeoutRef.current) {
        clearTimeout(calcTimeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [balances.length, excludeBoxUsers, totalBoxCost, totalFieldCost, fieldHours, expenses]);

  const toggleParticipantSelection = (pId) => {
    setSelectedParticipantIds(prev => prev.includes(pId) ? prev.filter(id => id !== pId) : [...prev, pId]);
  };

  const addParticipantsToSession = async () => {
    if (!sessionId || selectedParticipantIds.length === 0 || isAddingParticipants) return;
    setIsAddingParticipants(true);
    try {
      const participants = selectedParticipantIds.map((pid) => ({ session_id: sessionId, participant_id: pid }));
      await supabase.from("session_participants").insert(participants);

      const paymentsToCreate = selectedParticipantIds.map((pid) => ({
        session_id: sessionId, participant_id: pid, amount_owed: 0, has_paid: false, paid_at: null,
      }));
      await supabase.from("session_payments").upsert(paymentsToCreate, { onConflict: "session_id,participant_id", ignoreDuplicates: true });

      // Initialize field hours for new participants for all existing field expenses
      const fieldExpenses = expenses.filter(e => e.expense_type === "field");
      if (fieldExpenses.length > 0) {
        const fieldHoursToInsert = [];
        selectedParticipantIds.forEach(pid => {
          fieldExpenses.forEach(exp => {
            fieldHoursToInsert.push({
              expense_id: exp.id,
              participant_id: pid,
              session_id: sessionId,
              hours_played: parseFloat(exp.total_quantity) || 2
            });
          });
        });
        if (fieldHoursToInsert.length > 0) {
          await supabase.from("field_hours").insert(fieldHoursToInsert);
        }
      }

      setSelectedParticipantIds([]);
      setIsParticipantModalOpen(false);
      refreshSession(true);
      refreshGlobalData();
      toast.success(t("added_players_success"));
    } catch (e) {
      toast.error(t("error_occurred"));
    } finally {
      setIsAddingParticipants(false);
    }
  };

  const removeParticipantFromSession = (participantId, participantName) => {
    setConfirmModal({
      isOpen: true,
      title: t("remove_player"),
      message: `${t("confirm_remove_player_prefix")} ${participantName} ${t("confirm_remove_player_suffix")}`,
      onConfirm: async () => {
        await supabase.from("session_participants").delete().eq("session_id", sessionId).eq("participant_id", participantId);
        // Also clean up field hours and payments for this participant in this session
        await supabase.from("field_hours").delete().eq("participant_id", participantId).in("expense_id", expenses.map(e => e.id));
        await supabase.from("session_payments").delete().eq("session_id", sessionId).eq("participant_id", participantId);
        refreshSession(true);
        refreshGlobalData();
        toast.success(t("player_removed"));
      },
      type: "danger"
    });
  };

  const togglePaymentStatus = async (participantId) => {
    const existing = payments.find(p => p.participant_id === participantId && p.session_id === sessionId);
    if (existing) {
      await supabase.from("session_payments").update({
        has_paid: !existing.has_paid,
        paid_at: !existing.has_paid ? new Date().toISOString() : null,
      }).eq("id", existing.id);
    } else {
      await supabase.from("session_payments").insert([{
        session_id: sessionId, participant_id: participantId, has_paid: true, paid_at: new Date().toISOString(),
      }]);
    }
    refreshSession(true);
    refreshUnpaidUsers();
    refreshGlobalData();
  };

  const addBox = async (e) => {
    e.preventDefault();
    if (!newBox.description || !newBox.totalAmount || !newBox.totalQuantity) return;
    await supabase.from("expenses").insert([{
      session_id: sessionId, description: newBox.description, total_amount: parseFloat(newBox.totalAmount),
      item_name: t("shuttlecock"), total_quantity: parseFloat(newBox.totalQuantity), used_quantity: 0, expense_type: "box",
    }]);
    setNewBox({ description: "", totalAmount: "", totalQuantity: "" });
    refreshSession(true);
  };

  const addField = async (e) => {
    e.preventDefault();
    if (!newField.fieldName || !newField.hours || !newField.pricePerHour) return;
    const totalHours = parseFloat(newField.hours);
    const { data: expenseData, error } = await supabase.from("expenses").insert([{
      session_id: sessionId, description: `${newField.fieldName} - ${newField.hours} ${t("hour_short")}`,
      total_amount: totalHours * parseFloat(newField.pricePerHour), item_name: newField.fieldName,
      total_quantity: totalHours, used_quantity: 0, expense_type: "field",
    }]).select().single();

    if (!error && expenseData && sessionParticipants.length > 0) {
      const fieldHoursToInsert = sessionParticipants.map(p => ({
        expense_id: expenseData.id,
        participant_id: p.id,
        session_id: sessionId,
        hours_played: totalHours
      }));
      await supabase.from("field_hours").insert(fieldHoursToInsert);
    }
    setNewField({ fieldName: "", hours: "2", pricePerHour: "" });
    refreshSession(true);
  };

  const startEditingField = (exp, e) => {
    e.stopPropagation();
    const totalHours = parseFloat(exp.total_quantity);
    const totalCost = parseFloat(exp.total_amount);
    const pricePerHour = totalHours > 0 ? (totalCost / totalHours) : 0;
    setEditingFieldExpense(exp.id);
    setEditFieldValues({
      fieldName: exp.item_name,
      hours: String(totalHours),
      pricePerHour: String(pricePerHour)
    });
  };

  const cancelFieldEdit = (e) => {
    if (e) e.stopPropagation();
    setEditingFieldExpense(null);
    setEditFieldValues({ fieldName: "", hours: "", pricePerHour: "" });
  };

  const saveFieldExpense = async (expenseId, e) => {
    if (e) e.stopPropagation();
    const { fieldName, hours, pricePerHour } = editFieldValues;
    if (!fieldName.trim() || !hours || !pricePerHour) return;

    const totalHours = parseFloat(hours);
    const pph = parseFloat(pricePerHour);
    if (isNaN(totalHours) || isNaN(pph) || totalHours <= 0 || pph <= 0) return;

    const totalAmount = totalHours * pph;
    try {
      await supabase.from("expenses").update({
        item_name: fieldName.trim(),
        description: `${fieldName.trim()} - ${totalHours} ${t("hour_short")}`,
        total_quantity: totalHours,
        total_amount: totalAmount,
      }).eq("id", expenseId);

      setEditingFieldExpense(null);
      setEditFieldValues({ fieldName: "", hours: "", pricePerHour: "" });
      refreshSession(true);
      toast.success(t("field_updated"));
    } catch {
      toast.error(t("field_update_failed"));
    }
  };

  const updateFieldHoursLocal = (expenseId, participantId, hours) => {
    setFieldHours(prev => {
      const idx = prev.findIndex(fh => fh.expense_id === expenseId && fh.participant_id === participantId);
      if (idx >= 0) { const updated = [...prev]; updated[idx] = { ...updated[idx], hours_played: hours }; return updated; }
      return [...prev, { expense_id: expenseId, participant_id: participantId, hours_played: hours }];
    });
  };

  const saveFieldHours = async (expenseId, participantId, hours) => {
    const numHours = parseFloat(hours);
    if (isNaN(numHours) || numHours < 0) {
      await supabase.from("field_hours").delete().eq("expense_id", expenseId).eq("participant_id", participantId);
    } else {
      await supabase.from("field_hours").upsert({
        expense_id: expenseId,
        participant_id: participantId,
        session_id: sessionId,
        hours_played: numHours
      }, { onConflict: "expense_id,participant_id" });
    }
    refreshSession(true);
  };

  const setAllToProjected = async (expenseId, totalHours) => {
    const updates = sessionParticipants.map(p => ({
      expense_id: expenseId,
      participant_id: p.id,
      session_id: sessionId,
      hours_played: parseFloat(totalHours)
    }));
    await supabase.from("field_hours").upsert(updates, { onConflict: "expense_id,participant_id" });
    refreshSession(true);
    toast.success(t("set_all_hours_success"));
  };

  const clearAllHours = (expenseId) => {
    setConfirmModal({
      isOpen: true,
      title: t("clear_hours_title"),
      message: t("confirm_clear_hours"),
      onConfirm: async () => {
        await supabase.from("field_hours").delete().eq("expense_id", expenseId);
        refreshSession(true);
        toast.success(t("hours_cleared"));
      },
      type: "warning"
    });
  };

  const updateUsedQuantity = async (id, quantity) => {
    await supabase.from("expenses").update({ used_quantity: parseFloat(quantity) || 0 }).eq("id", id);
    setEditingUsed(null); setUsedValue(""); refreshSession(true);
  };

  const deleteExpense = (id, description) => {
    setConfirmModal({
      isOpen: true,
      title: t("delete_expense_title"),
      message: `${t("confirm_delete_expense_prefix")} "${description}"? ${t("confirm_delete_expense_suffix")}`,
      onConfirm: async () => {
        await supabase.from("expenses").delete().eq("id", id);
        refreshSession(true);
        toast.success(t("expense_deleted"));
      },
      type: "danger"
    });
  };

  const handleExcludeBoxToggle = async (pId) => {
    const isEx = excludeBoxUsers[pId] || false;
    setExcludeBoxUsers(prev => ({ ...prev, [pId]: !isEx }));
    try {
      await supabase.from("session_participant_settings").upsert({
        session_id: sessionId, participant_id: pId, exclude_box_cost: !isEx,
      }, { onConflict: ["session_id", "participant_id"] });
    } catch { setExcludeBoxUsers(prev => ({ ...prev, [pId]: isEx })); }
  };

  const availableParticipants = allParticipants.filter(p => !sessionParticipants.find(sp => sp.id === p.id));

  return (
    <PageLoader loading={loading || isNotFound}>
      <div className="session-detail-page">
        <Header
          title={selectedSessionData?.session_name || ""}
          subtitle={selectedSessionData ? new Date(selectedSessionData.session_date).toLocaleDateString(language === "vi" ? "vi-VN" : "en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" }) : ""}
          sessionParticipantsCount={sessionParticipants.length}
          totalExpenses={totalExpenses}
          paidCount={paidCount}
        />
        <div className="container">
          {/* Participants Section */}
          <section className="card participants-card">
            <div className="card-header">
              <div className="card-title">
                <Users size={24} /><h2>{t("players")}</h2><span className="badge">{sessionParticipants.length}</span>
              </div>
              <div className="card-actions-group">
                {isOwner && availableParticipants.length > 0 && (
                  <div className="header-action-wrapper">
                    <button
                      onClick={() => setIsParticipantModalOpen(true)}
                      className="btn-action-pill"
                    >
                      <Plus size={18} />
                      <span>{t("add")}</span>
                    </button>

                    <MultiSelectionModal
                      isOpen={isParticipantModalOpen}
                      onClose={() => {
                        setIsParticipantModalOpen(false);
                        setSelectedParticipantIds([]);
                      }}
                      title={t("select_participants")}
                      items={availableParticipants}
                      selectedIds={selectedParticipantIds}
                      onToggle={toggleParticipantSelection}
                      onConfirm={addParticipantsToSession}
                      confirmText={t("add")}
                      searchPlaceholder={t("search_players")}
                      loading={isAddingParticipants}
                    />
                  </div>
                )}
                <button onClick={() => setExpandParticipants(!expandParticipants)} className="btn-icon">
                  {expandParticipants ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </button>
              </div>
            </div>

            <AnimatePresence>
              {expandParticipants && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                  <div className="participants-grid">
                    {sessionParticipants.map(p => (
                      <motion.div key={p.id} className="participant-chip">
                        <div className={`chip-avatar-wrapper ${p.linked_user_id ? 'avatar-ring-verified' : ''}`}>
                          {p.profile_image_url ? (
                            <img src={p.profile_image_url} alt={p.name} className="chip-avatar" />
                          ) : (
                            <div className="avatar-initials-mini">{p.name.charAt(0).toUpperCase()}</div>
                          )}
                          {p.linked_user_id && (
                            <span className="verified-dot"><Check size={7} /></span>
                          )}
                        </div>
                        <span className="chip-name">{p.name}</span>
                        {isOwner && (
                          <button
                            onClick={() => removeParticipantFromSession(p.id, p.name)}
                            className="chip-remove-btn"
                          >
                            <X size={14} />
                          </button>
                        )}
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </section>

          {sessionParticipants.length > 0 && isOwner && (
            <div className="forms-grid">
              <section className="card add-box-card">
                <div
                  className="card-header-clickable"
                  onClick={() => setIsBoxFormOpen(!isBoxFormOpen)}
                >
                  <div className="card-title">
                    <Package size={24} />
                    <h2>{t("add_shuttlecock_box")}</h2>
                  </div>
                  <motion.div
                    animate={{ rotate: isBoxFormOpen ? 180 : 0 }}
                    transition={{ duration: 0.3 }}
                    className="chevron-icon"
                  >
                    <ChevronDown size={20} />
                  </motion.div>
                </div>

                <AnimatePresence>
                  {isBoxFormOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3, ease: "easeInOut" }}
                      className="form-accordion-wrapper"
                    >
                      <form onSubmit={addBox} className="add-form">
                        <input type="text" placeholder={t("box_name_placeholder")} value={newBox.description} onChange={e => setNewBox({ ...newBox, description: e.target.value })} className="input" />
                        <input type="number" step="0.01" placeholder={t("price")} value={newBox.totalAmount} onChange={e => setNewBox({ ...newBox, totalAmount: e.target.value })} className="input" />
                        <input type="number" placeholder={t("shuttlecock_count")} value={newBox.totalQuantity} onChange={e => setNewBox({ ...newBox, totalQuantity: e.target.value })} className="input" />
                        <button type="submit" className="btn btn-primary"><Plus size={20} /></button>
                      </form>
                    </motion.div>
                  )}
                </AnimatePresence>
              </section>

              <section className="card add-field-card">
                <div
                  className="card-header-clickable"
                  onClick={() => setIsFieldFormOpen(!isFieldFormOpen)}
                >
                  <div className="card-title">
                    <MapPin size={24} />
                    <h2>{t("add_field_booking")}</h2>
                  </div>
                  <motion.div
                    animate={{ rotate: isFieldFormOpen ? 180 : 0 }}
                    transition={{ duration: 0.3 }}
                    className="chevron-icon"
                  >
                    <ChevronDown size={20} />
                  </motion.div>
                </div>

                <AnimatePresence>
                  {isFieldFormOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3, ease: "easeInOut" }}
                      className="form-accordion-wrapper"
                    >
                      <form onSubmit={addField} className="add-form">
                        <input type="text" placeholder={t("field_name_placeholder")} value={newField.fieldName} onChange={e => setNewField({ ...newField, fieldName: e.target.value })} className="input" />
                        <input type="number" step="0.5" placeholder={t("hours")} value={newField.hours} onChange={e => setNewField({ ...newField, hours: e.target.value })} className="input" />
                        <input type="number" step="0.01" placeholder={t("price_per_hour")} value={newField.pricePerHour} onChange={e => setNewField({ ...newField, pricePerHour: e.target.value })} className="input" />
                        <button type="submit" className="btn btn-secondary"><Plus size={20} /></button>
                      </form>
                    </motion.div>
                  )}
                </AnimatePresence>
              </section>
            </div>
          )}

          {boxes.length > 0 && isOwner && (
            <section className="card">
              <div className="card-header">
                <div className="card-title"><Package size={24} /><h2>{t("shuttlecock_boxes")}</h2><span className="badge">{boxes.length}</span></div>
                <div className="header-amount">{totalBoxCost.toFixed(2)} kr</div>
              </div>
              <div className="expenses-grid-boutique">
                {boxes.map(exp => {
                  const pricePerUnit = (exp.total_amount / exp.total_quantity).toFixed(1);
                  return (
                    <div key={exp.id} className="expense-card-boutique">
                      <div className="card-boutique-main">
                        <div className="card-boutique-info">
                          <h3>{exp.description}</h3>
                          <div className="price-unit-tag">
                            <span>{pricePerUnit} kr</span>
                            <span className="unit-label">/{t("unit")}</span>
                          </div>
                        </div>
                        {isOwner && (
                          <button onClick={() => deleteExpense(exp.id, exp.description)} className="btn-icon-boutique ghost">
                            <Trash2 size={16} />
                          </button>
                        )}
                      </div>

                      <div className="usage-container-boutique">
                        <span className="usage-label-minimal">{t("used")}:</span>
                        {editingUsed === exp.id ? (
                          <input
                            type="number"
                            value={usedValue}
                            onChange={e => setUsedValue(e.target.value)}
                            onBlur={() => {
                              updateUsedQuantity(exp.id, usedValue);
                              setEditingUsed(null);
                            }}
                            className="input-usage-boutique"
                            autoFocus
                          />
                        ) : (
                          <div
                            className="usage-badge-boutique"
                            onClick={() => {
                              setEditingUsed(exp.id);
                              setUsedValue(String(exp.used_quantity || 0));
                            }}
                          >
                            <span className="usage-numeric">{exp.used_quantity || 0}/{exp.total_quantity}</span>
                            <Edit3 size={12} className="edit-icon-subtle" />
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          {fields.length > 0 && isOwner && (
            <section className="card">
              <div className="card-header">
                <div className="card-title"><MapPin size={24} /><h2>{t("field_bookings")}</h2></div>
                <div className="header-amount">{totalFieldCost.toFixed(2)} kr</div>
              </div>
              <div className="fields-list-expanded">
                {fields.map(exp => {
                  const totalHours = parseFloat(exp.total_quantity);
                  const totalCost = parseFloat(exp.total_amount);
                  const pricePerHour = totalCost / totalHours;
                  const hoursData = getFieldHoursForExpense(fieldHours, exp.id);
                  const isExpanded = expandedField === exp.id;

                  return (
                    <div key={exp.id} className="field-card-boutique">
                      <div className={`field-card-header-boutique ${editingFieldExpense === exp.id ? 'is-editing' : ''}`} onClick={() => { if (editingFieldExpense !== exp.id) setExpandedField(isExpanded ? null : exp.id); }}>
                        {editingFieldExpense === exp.id ? (
                          <div className="field-edit-premium-form">
                            <div className="premium-form-row">
                              <div className="premium-form-group flex-2">
                                <label className="premium-form-label">{t("field")}</label>
                                <input
                                  type="text"
                                  value={editFieldValues.fieldName}
                                  onChange={e => setEditFieldValues(prev => ({ ...prev, fieldName: e.target.value }))}
                                  className="input"
                                  placeholder={t("field_name_placeholder")}
                                  autoFocus
                                  onClick={e => e.stopPropagation()}
                                  onKeyDown={e => { if (e.key === 'Enter') saveFieldExpense(exp.id, e); if (e.key === 'Escape') cancelFieldEdit(e); }}
                                />
                              </div>
                            </div>
                            <div className="premium-form-row">
                              <div className="premium-form-group flex-1">
                                <label className="premium-form-label">{t("hours")}</label>
                                <input
                                  type="number"
                                  step="0.5"
                                  min="0.5"
                                  value={editFieldValues.hours}
                                  onChange={e => setEditFieldValues(prev => ({ ...prev, hours: e.target.value }))}
                                  className="input"
                                  placeholder={t("hours")}
                                  onClick={e => e.stopPropagation()}
                                  onKeyDown={e => { if (e.key === 'Enter') saveFieldExpense(exp.id, e); if (e.key === 'Escape') cancelFieldEdit(e); }}
                                />
                              </div>
                              <div className="premium-form-group flex-1">
                                <label className="premium-form-label">{t("price_per_hour")}</label>
                                <input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  value={editFieldValues.pricePerHour}
                                  onChange={e => setEditFieldValues(prev => ({ ...prev, pricePerHour: e.target.value }))}
                                  className="input"
                                  placeholder={t("price_per_hour")}
                                  onClick={e => e.stopPropagation()}
                                  onKeyDown={e => { if (e.key === 'Enter') saveFieldExpense(exp.id, e); if (e.key === 'Escape') cancelFieldEdit(e); }}
                                />
                              </div>
                            </div>
                            <div className="premium-form-footer">
                              <div className="premium-form-total">
                                <span className="total-label">{t("total")}:</span>
                                <span className="total-value">
                                  {(parseFloat(editFieldValues.hours || 0) * parseFloat(editFieldValues.pricePerHour || 0)).toFixed(0)} <span className="currency">kr</span>
                                </span>
                              </div>
                              <div className="premium-form-actions">
                                <button
                                  className="btn btn-secondary"
                                  onClick={(e) => cancelFieldEdit(e)}
                                >
                                  {t("cancel")}
                                </button>
                                <button
                                  className="btn btn-primary"
                                  onClick={(e) => saveFieldExpense(exp.id, e)}
                                >
                                  {t("save")}
                                </button>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <>
                            <div className="field-info-main-boutique">
                              <div className="field-icon-compact"><Clock size={20} /></div>
                              <div className="field-text-content-boutique">
                                <h3>{exp.item_name}</h3>
                                <div className="field-metric-hub">
                                  <span className="metric-badge">{totalHours} {t("hour_short")}</span>
                                  <span className="metric-dot">•</span>
                                  <span className="metric-badge-secondary">{pricePerHour.toFixed(0)} {t("per_hour")}</span>
                                  <span className="metric-dot">•</span>
                                  <span className="metric-badge-accent">{totalCost.toFixed(0)} kr</span>
                                </div>
                              </div>
                            </div>
                            <div className="field-action-hub-boutique">
                              {isOwner && (
                                <>
                                  <button
                                    onClick={(e) => startEditingField(exp, e)}
                                    className="btn-edit-boutique"
                                    title={t("edit")}
                                  >
                                    <Pencil size={15} />
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      deleteExpense(exp.id, exp.item_name);
                                    }}
                                    className="btn-trash-boutique"
                                  >
                                    <Trash2 size={16} />
                                  </button>
                                </>
                              )}
                              <div className={`chevron-boutique ${isExpanded ? 'expanded' : ''}`}>
                                <ChevronDown size={20} />
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                      <AnimatePresence>
                        {isExpanded && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            className="field-hours-section"
                          >
                            <div className="field-hours-toolbar">
                              <span className="toolbar-label">{t("manage_hours")}</span>
                              <div className="toolbar-actions">
                                <button className="btn-pill" onClick={() => setAllToProjected(exp.id, totalHours)}>
                                  <Clock size={14} />
                                  <span>{t("set_all")} ({totalHours}h)</span>
                                </button>
                                <button className="btn-pill" onClick={() => clearAllHours(exp.id)}>
                                  <Trash2 size={14} />
                                  <span>{t("clear_all")}</span>
                                </button>
                              </div>
                            </div>

                            <div className="hours-list-premium">
                              {sessionParticipants.map(participant => {
                                const hrData = hoursData.find(fh => fh.participant_id === participant.id);
                                const currentHrs = hrData ? parseFloat(hrData.hours_played) : 0;
                                const totalHoursUsed = hoursData.reduce((s, fh) => s + parseFloat(fh.hours_played), 0);
                                const share = totalHoursUsed > 0 ? (currentHrs / totalHoursUsed * 100).toFixed(0) : 0;

                                return (
                                  <div key={participant.id} className="hour-row-card">
                                    <div className="player-info-compact">
                                      <div className={`player-avatar-premium ${participant.linked_user_id ? 'avatar-ring-verified' : ''}`}>
                                        {participant.profile_image_url ? (
                                          <img src={participant.profile_image_url} alt={participant.name} />
                                        ) : (
                                          <span className="avatar-initials">{participant.name.charAt(0).toUpperCase()}</span>
                                        )}
                                        {participant.linked_user_id && (
                                          <span className="verified-dot"><Check size={8} /></span>
                                        )}
                                      </div>
                                      <div className="player-meta-info">
                                        <span className="player-name-main">{participant.name}</span>
                                        <div className="player-hour-status">
                                          <span>
                                            <span className="status-indicator-dot"></span>
                                            {currentHrs}{t("hour_short")}
                                          </span>
                                          <span className="status-separator">/</span>
                                          <span>{totalHours}{t("hour_short")}</span>
                                        </div>
                                      </div>
                                    </div>

                                    <div className="hour-controls-premium">
                                      <button
                                        className="adjust-btn"
                                        onClick={() => {
                                          const next = Math.max(0, currentHrs - 0.5);
                                          updateFieldHoursLocal(exp.id, participant.id, next);
                                          saveFieldHours(exp.id, participant.id, next);
                                        }}
                                      >
                                        <Minus size={18} />
                                      </button>
                                      <input
                                        type="number"
                                        step="0.5"
                                        min="0"
                                        value={currentHrs}
                                        onChange={e => updateFieldHoursLocal(exp.id, participant.id, e.target.value)}
                                        onBlur={e => saveFieldHours(exp.id, participant.id, e.target.value)}
                                        className="hour-input-minimal"
                                      />
                                      <button
                                        className="adjust-btn"
                                        onClick={() => {
                                          const next = currentHrs + 0.5;
                                          updateFieldHoursLocal(exp.id, participant.id, next);
                                          saveFieldHours(exp.id, participant.id, next);
                                        }}
                                      >
                                        <Plus size={18} />
                                      </button>
                                    </div>
                                    <div className="hour-share-indicator">
                                      {share}% {t("cost_unit")}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  )
                })}
              </div>
            </section>
          )}

          {sessionParticipants.length > 0 && expenses.length > 0 && (
            <section className="card settlement-card">
              <div className="card-header"><div className="card-title"><DollarSign size={24} /><h2>{t("cost_split")}</h2></div></div>

              <div className="settlement-summary">
                <div className="summary-item">
                  <div className="summary-icon"><Feather size={24} /></div>
                  <div className="summary-content">
                    <span className="summary-value">
                      {totalUsedQuantity}/{totalTotalQuantity} {t("total_used")}
                    </span>
                    <span className="summary-label">{totalBoxCost.toFixed(0)} KR</span>
                  </div>
                </div>
                <div className="summary-item">
                  <div className="summary-icon"><Clock size={24} /></div>
                  <div className="summary-content">
                    <span className="summary-value">
                      {totalHours} {t("total_hours_full")}
                    </span>
                    <span className="summary-label">{totalFieldCost.toFixed(0)} KR</span>
                  </div>
                </div>
                <div className="summary-item highlight">
                  <div className="summary-icon"><DollarSign size={24} /></div>
                  <div className="summary-content">
                    <span className="summary-value">{totalExpenses.toFixed(0)} kr</span>
                    <span className="summary-label">{sessionParticipants.length} {t("players")}</span>
                  </div>
                </div>

                {adminFee > 0 && feePercent > 0 && (
                  <div className="summary-item admin-fee-item">
                    <div className="summary-icon admin-fee-icon">
                      <Percent size={22} />
                    </div>
                    <div className="summary-content">
                      <span className="summary-value">
                        +{feePerPersonLabel}
                        <span className="per-person-tag">kr/{t("per_person")}</span>
                      </span>
                      <span className="summary-label">{t("admin_fee")} · {feePercent}%</span>
                    </div>
                  </div>
                )}

                {selectedSessionData?.swish_number && (
                  <div
                    className="summary-item swish-summary-item"
                    onClick={() => {
                      navigator.clipboard.writeText(selectedSessionData.swish_number);
                      toast.success(t("swish_copied"));
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="summary-icon swish-summary-icon">
                      <img src={swishLight} className="swish-logo-img light-mode-logo" alt="Swish" />
                      <img src={swishDark} className="swish-logo-img dark-mode-logo" alt="Swish" />
                    </div>
                    <div className="summary-content">
                      <span className="summary-value swish-value">{selectedSessionData.swish_number}</span>
                      <span className="summary-label">{t("swish_number")}</span>
                    </div>
                    <button
                      className="btn-copy-swish-inline"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigator.clipboard.writeText(selectedSessionData.swish_number);
                        toast.success(t("swish_copied"));
                      }}
                      title={t("copy_number_tooltip")}
                    >
                      <Copy size={18} />
                    </button>
                  </div>
                )}
              </div>

              <div className="settlement-controls">

                <div className="settlement-search" ref={searchRef}>
                  <Search size={14} className="search-icon" />
                  <input
                    type="text"
                    placeholder={t("filter_by_name")}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onFocus={handleSearchFocus}
                    className="search-input"
                  />
                  {searchQuery && (
                    <button className="search-clear" onClick={() => setSearchQuery("")}>
                      <X size={12} />
                    </button>
                  )}
                </div>
              </div>

              <div className="settlement-list">
                {filteredBalances.map(person => (
                  <div key={person.id} className={`settlement-item-premium ${person.hasPaid ? "paid" : "unpaid"}`}>
                    <div className="settlement-item-main">
                      <div className={`settlement-avatar-large ${person.linked_user_id ? 'avatar-ring-verified' : ''}`}>
                        {person.profile_image_url ? (
                          <img src={person.profile_image_url} alt={person.name} />
                        ) : (
                          <span className="avatar-initials">{person.name.charAt(0).toUpperCase()}</span>
                        )}
                        {person.linked_user_id && (
                          <span className="verified-dot"><Check size={10} /></span>
                        )}
                      </div>
                      <div className="settlement-info">
                        <span className="player-name">
                          {person.name}
                          {person.linked_user_id && (
                            <span className="verified-badge" style={{ marginLeft: '6px' }}>
                              <Shield size={10} />
                            </span>
                          )}
                          <span className={`settlement-status-badge ${person.hasPaid ? 'paid' : 'unpaid'}`} style={{ marginLeft: '6px' }}>
                            {person.hasPaid ? t("paid") : t("unpaid")}
                          </span>
                        </span>
                        {boxes.length > 0 && (
                          <div className="settlement-actions-inline">
                            <button
                              className={`exclude-badge-btn ${excludeBoxUsers[person.id] ? "excluded" : "included"}`}
                              onClick={() => handleExcludeBoxToggle(person.id)}
                              disabled={!isOwner}
                            >
                              <Package size={14} />
                              <span className="exclude-text">{excludeBoxUsers[person.id] ? t("excludes_box_short") : t("includes_box_short")}</span>
                            </button>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="settlement-amount-section">
                      <div
                        className="player-amount copyable group"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigator.clipboard.writeText(person.owes.toFixed(2));
                          toast.success("Copied to clipboard");
                        }}
                        title="Copy amount"
                      >
                        {person.owes.toFixed(2)} <span className="currency">kr</span>
                        <div className="copy-icon-wrapper">
                          <Copy size={12} className="copy-icon-inline" />
                        </div>
                      </div>
                    </div>

                    {isOwner && (
                      <div className="settlement-checkbox-wrapper">
                        <div className={`premium-checkbox ${person.hasPaid ? "checked" : ""}`} onClick={() => togglePaymentStatus(person.id)}>
                          {person.hasPaid && <Check size={20} />}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              {isOwner && (
                <div className="settlement-note">
                  {t("settlement_note")}
                </div>
              )}
            </section>
          )}
        </div>
        {/* Reusable Confirmation Modal */}
        <ConfirmModal
          isOpen={confirmModal.isOpen}
          onClose={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
          onConfirm={confirmModal.onConfirm}
          title={confirmModal.title}
          message={confirmModal.message}
          type={confirmModal.type}
        />
      </div>
    </PageLoader>
  );
};
